import { CROWD_RULES, type CrowdPick } from '@shared/types';
import { hasHomograph, transliterateWord } from '../engine';
import { apiHeaders, endpoint } from './config';

/**
 * What everyone else meant.
 *
 * The engine's ranking of the readings a roman spelling could have is judgement,
 * not measurement — there is no Nepali frequency corpus behind it. This is the
 * layer that turns it into measurement as the platform is used: readings that
 * enough different people reached past the default to choose.
 *
 * Three rules stand between a tally and the word someone gets, because a crowd
 * that can change what is typed can also be wrong, lopsided, or steered:
 *
 *   1. **Distinct people, not picks.** Counted server-side, one row per browser.
 *   2. **Dominance.** A word people genuinely disagree about — half writing पानी
 *      and half पनि — reorders the picker and does not touch the default. Only a
 *      lopsided result means "the guess is wrong" rather than "this is
 *      ambiguous".
 *   3. **Never a known homograph.** Where both readings are real words and only
 *      the sentence knows which was meant, no tally is evidence about anything;
 *      the crowd can fix what the engine gets wrong, not pick a side in a
 *      question that has no general answer.
 *
 * None of it ever overrides the writer's own choice, which is checked first.
 */

const CACHE_KEY = 'kaha:tl-crowd';
/** Long enough that this is not on the critical path; short enough to improve. */
const TTL_MS = 12 * 60 * 60 * 1000;

interface Cached {
  fetchedAt: number;
  picks: CrowdPick[];
}

/** roman → readings, best-supported first. */
type CrowdMap = Map<string, CrowdPick[]>;

let crowd: CrowdMap = new Map();
let loading: Promise<void> | null = null;

function group(picks: CrowdPick[]): CrowdMap {
  const map: CrowdMap = new Map();
  for (const pick of picks) {
    const list = map.get(pick.roman) ?? [];
    list.push(pick);
    map.set(pick.roman, list);
  }
  for (const list of map.values()) list.sort((a, b) => b.votes - a.votes);
  return map;
}

function readCache(): Cached | null {
  try {
    const stored = localStorage.getItem(CACHE_KEY);
    return stored ? (JSON.parse(stored) as Cached) : null;
  } catch {
    return null;
  }
}

/**
 * Loads the shared ranking, at most once per session and once per half day.
 *
 * Called when a field first converts a word rather than at start-up: a reader
 * who never types Nepali should not pay for this, and the first word converts
 * correctly from the cache or from the engine while it is still in flight.
 */
export function ensureCrowd(): void {
  if (loading || crowd.size > 0) return;

  const cached = readCache();
  if (cached) crowd = group(cached.picks);
  if (cached && Date.now() - cached.fetchedAt < TTL_MS) return;

  /*
   * Nothing here may break typing.
   *
   * This runs on the first word converted, on somebody else's page, inside their
   * keydown handler — so anything it throws takes the conversion with it and the
   * field silently stops working. A host site with `connect-src 'self'`, or a
   * browser old enough to lack fetch, would otherwise turn "the ranking is
   * unavailable" into "the widget is broken".
   */
  if (typeof fetch !== 'function') return;

  try {
    loadCrowd();
  } catch {
    /* The engine's own ranking stands. */
  }
}

function loadCrowd(): void {
  loading = fetch(endpoint('/api/picks'), { headers: apiHeaders() })
    .then((response) => (response.ok ? response.json() : null))
    .then((payload: { picks: CrowdPick[] } | null) => {
      if (!payload?.picks) return;
      crowd = group(payload.picks);
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ fetchedAt: Date.now(), picks: payload.picks }));
      } catch {
        /* A full or unavailable store just means fetching again next session. */
      }
    })
    .catch(() => {
      /* Offline, or the endpoint is down: the engine's own ranking stands. */
    })
    .finally(() => {
      loading = null;
    });
}

/** Every reading the crowd has recorded for a spelling, best-supported first. */
export function crowdReadings(roman: string): CrowdPick[] {
  return crowd.get(roman.toLowerCase()) ?? [];
}

/**
 * The reading the crowd is confident enough about to change what gets typed.
 *
 * Null unless it clears every rule above — which is the common case, and
 * deliberately so.
 */
export function crowdDefault(roman: string): string | null {
  const lower = roman.toLowerCase();

  // A genuine homograph has no general answer, so a tally is not evidence.
  if (hasHomograph(lower)) return null;

  const [best, runnerUp] = crowdReadings(lower);
  if (!best || best.votes < CROWD_RULES.minVoters) return null;
  if (runnerUp && best.votes < runnerUp.votes * CROWD_RULES.dominance) return null;

  // Nothing to say: the crowd agrees with the engine.
  return best.text === transliterateWord(roman) ? null : best.text;
}

/**
 * A spelling people demonstrably do not agree on.
 *
 * The mirror of the dominance rule: where that decides the crowd is *not* sure
 * enough to change the default, this reports the same fact as something worth
 * showing the writer. Two readings, both with real support, neither winning.
 */
export function crowdContested(roman: string): boolean {
  const [best, runnerUp] = crowdReadings(roman.toLowerCase());
  if (!best || !runnerUp) return false;

  return runnerUp.votes >= CROWD_RULES.minVoters && best.votes < runnerUp.votes * CROWD_RULES.dominance;
}
